import { Box, Chip, CircularProgress, Divider, Icon, InputAdornment, Menu, MenuItem, TextField } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "~/auth/AuthProvider";
import sfAuth from "~/auth/SFAuth";
import { BodyText, CaptionText, SubtitleText } from "~/components/Typographies";
import { useMyTenantsQuery } from "~/gql/queries/tenants.generated";
import logger from "~/helpers/logger";
import snackMessages from "~/helpers/snackMessages";
import purgeTenantScopedState from "~/helpers/tenantSwitchCleanup";
import useTenantMutations from "~/hooks/mutations/useTenantMutations";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { abortUnsavedChangesExit, confirmUnsavedChangesExit } from "~/layout/unsavedChangesRegistry";
import { useSnacks } from "~/providers/SnackProvider";
import { baseApiWithGraphql } from "~/services/baseApi";
import { selectIsQueueStateKnown, selectTotalBlockingQueueCount } from "~/store/offlineQueueSlice";
import tenantChanged from "~/store/tenantChanged";

const ENTITY_LABEL = "tenant";

const TenantSwitcher = () => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const [openSnack] = useSnacks();
	const { currentUser, wsUserType } = useContext(AuthContext);
	const currentTenantId = useAppSelector((state) => state.settings.tenantId);
	const blockingQueueCount = useAppSelector(selectTotalBlockingQueueCount);
	const isQueueStateKnown = useAppSelector(selectIsQueueStateKnown);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [isSwitching, setIsSwitching] = useState(false);
	const [search, setSearch] = useState("");

	const {
		data,
		isLoading,
		error: loadError
	} = useMyTenantsQuery(undefined, {
		skip: !currentUser || wsUserType === "customer"
	});
	const tenants = data?.myTenants;
	const { switchTenant } = useTenantMutations();

	const visibleTenants = useMemo(() => {
		if (!tenants) {
			return [];
		}
		const sorted = [...tenants].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
		const term = search.trim().toLowerCase();
		if (!term) {
			return sorted;
		}
		return sorted.filter((t) => t.name.toLowerCase().includes(term) || t.id.toLowerCase().includes(term));
	}, [tenants, search]);

	if (!tenants || tenants.length <= 1) {
		return null;
	}

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSearch("");
	};

	const handleSelect = async (tenantId: string) => {
		handleClose();

		if (!isQueueStateKnown) {
			openSnack(t("shell.tenantSwitcher.offlineQueueUnknown"), "warning");
			return;
		}

		if (blockingQueueCount > 0) {
			openSnack(t("shell.tenantSwitcher.offlineQueueBlocked", { count: blockingQueueCount }), "warning");
			return;
		}

		if (!(await confirmUnsavedChangesExit())) return;

		setIsSwitching(true);
		const switched = await switchTenant(tenantId);
		if (!switched) {
			// The user is still on the form they just agreed to discard, so hand its guard back.
			abortUnsavedChangesExit();
			setIsSwitching(false);
			return;
		}
		dispatch(tenantChanged());
		try {
			await sfAuth.checkSession(true);
			dispatch(baseApiWithGraphql.util.resetApiState());
			await purgeTenantScopedState();
			window.location.reload();
		} catch (err) {
			abortUnsavedChangesExit();
			setIsSwitching(false);
			logger.error(snackMessages.actionError(`switching ${ENTITY_LABEL}`), err);
			openSnack(snackMessages.actionError(`switching ${ENTITY_LABEL}`), "error");
		}
	};

	return (
		<>
			<Chip
				color="error"
				disabled={isSwitching}
				label={
					isSwitching
						? t("shell.tenantSwitcher.switching")
						: t("shell.tenantSwitcher.chip", { tenantId: currentTenantId })
				}
				onClick={isSwitching ? undefined : handleClick}
				size="medium"
				sx={{
					"&:hover": { bgcolor: "error.dark" },
					cursor: "pointer",
					fontSize: "0.95rem",
					fontWeight: "bold",
					mr: 2
				}}
				variant="filled"
			/>
			<Menu
				anchorEl={anchorEl}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				autoFocus={false}
				disableAutoFocusItem
				onClose={handleClose}
				open={open}
				slotProps={{ paper: { sx: { maxHeight: 480, minWidth: 280 } } }}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box key="header" sx={{ px: 2, py: 1 }}>
					<SubtitleText color="text.secondary">{t("shell.tenantSwitcher.title")}</SubtitleText>
				</Box>
				<Box key="search" sx={{ px: 2, py: 1 }}>
					<TextField
						autoFocus
						fullWidth
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== "Escape") {
								e.stopPropagation();
							}
						}}
						placeholder={t("shell.tenantSwitcher.filterPlaceholder")}
						size="small"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Icon fontSize="small">search</Icon>
									</InputAdornment>
								)
							}
						}}
						value={search}
					/>
				</Box>
				<Divider key="divider" />

				{isLoading && (
					<Box key="loading" sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<CircularProgress size={24} />
					</Box>
				)}

				{!!loadError && (
					<Box key="error" sx={{ px: 2, py: 1 }}>
						<BodyText color="error">{t("shell.tenantSwitcher.loadError")}</BodyText>
					</Box>
				)}

				{!isLoading && !loadError && visibleTenants.length === 0 && (
					<Box key="no-tenants" sx={{ px: 2, py: 1 }}>
						<BodyText color="text.secondary">
							{tenants && tenants.length > 0
								? t("shell.tenantSwitcher.noMatches")
								: t("shell.tenantSwitcher.noneAvailable")}
						</BodyText>
					</Box>
				)}

				{visibleTenants.map((tenant) => (
					<MenuItem
						key={tenant.id}
						onClick={() => handleSelect(tenant.id)}
						selected={tenant.id === currentTenantId}
						sx={{ "&.Mui-selected": { backgroundColor: "action.selected" } }}
					>
						<Box>
							<BodyText bold>{tenant.name}</BodyText>
							<CaptionText color="text.secondary">{tenant.id}</CaptionText>
						</Box>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default TenantSwitcher;
